// Globals
import { useTranslation } from 'react-i18next';

// Models
import {
  IOrder,
  ordersWithAdditionalOfferings,
  ordersWithPackages,
  ordersWithPayment,
  PricePackageBundleType,
} from '@/models/order.model';
import { ROUTES } from '@/models/router.model';

// Components
import { StyledBlock, StyledItem, StyledListWrap, StyledTag } from './OrderDetailsStyles';
import { StyledLabel, StyledLink } from '../Styled';

// Helpers
import { getEntityId, renderCurrency } from '@/utils';

type TOrderDetailsProps = {
  order: IOrder;
};

const OrderDetails = ({ order }: TOrderDetailsProps) => {
  const { t } = useTranslation('order');
  const selectedBundle = order.selected_bundle;

  const getBundleLabel = () => {
    if (!selectedBundle) return '-';

    const baseKey = `selected_bundle_${selectedBundle.toLowerCase()}`;
    const suffix = selectedBundle === PricePackageBundleType.ESSENTIAL ? `_${order.step_name.toLowerCase()}` : '';

    return t(`${baseKey}${suffix}`);
  };

  return (
    <>
      <StyledBlock>
        <StyledItem>
          <StyledLabel>{t('user')}</StyledLabel>
          <StyledLink
            path={{
              pathname: ROUTES.USERS,
              search: new URLSearchParams({ id: getEntityId(order.user_id) }).toString(),
            }}
            target="_blank"
          >
            {order.user_name}
          </StyledLink>
        </StyledItem>
        <StyledItem>
          <StyledLabel>{t('order_address')}</StyledLabel>
          <StyledLink
            path={{
              pathname: ROUTES.PROPERTIES,
              search: new URLSearchParams({ id: getEntityId(order.property_id) }).toString(),
            }}
          >
            {order.property_address.full_address}
          </StyledLink>
        </StyledItem>
      </StyledBlock>
      {ordersWithPayment.has(order.step_name) && (
        <>
          <StyledBlock>
            <StyledItem>
              <StyledLabel>{t('payment_status')}</StyledLabel>
              <StyledTag>{order.payment_status ? t(`payment_status_${order.payment_status}`) : '-'}</StyledTag>
            </StyledItem>
          </StyledBlock>
          <StyledBlock>
            <StyledItem>
              <StyledLabel>{t('payment_method')}</StyledLabel>
              <StyledTag>{order.payment_instrument ? t(`payment_method_${order.payment_instrument}`) : '-'}</StyledTag>
            </StyledItem>
          </StyledBlock>
          {ordersWithPackages.has(order.step_name) && (
            <StyledBlock>
              <StyledItem>
                <StyledLabel>{t('package_name')}</StyledLabel>
                <StyledTag>{getBundleLabel()}</StyledTag>
              </StyledItem>
            </StyledBlock>
          )}
          {ordersWithAdditionalOfferings.has(order.step_name) && (
            <StyledBlock>
              <StyledItem>
                <StyledLabel>{t('additional_offerings')}</StyledLabel>
                <StyledListWrap>
                  {order?.additional_offerings?.length
                    ? order?.additional_offerings?.map((offering) => (
                        <StyledTag key={offering.PK}>
                          {`${offering.name_en} – ${renderCurrency(offering.price ?? 0)}`}
                        </StyledTag>
                      ))
                    : '-'}
                </StyledListWrap>
              </StyledItem>
            </StyledBlock>
          )}
          <StyledBlock>
            <StyledItem>
              <StyledLabel>{t('total_order_price')}</StyledLabel>
              <StyledTag>{order.price ? t(renderCurrency(order.price)) : '-'}</StyledTag>
            </StyledItem>
          </StyledBlock>
        </>
      )}
    </>
  );
};

export default OrderDetails;
